import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type { TruckingFleetCreateResponse } from '../../types/trucking-roster';
import { Plus, Loader2, X, Upload, Truck, CheckCircle2, CalendarClock, Wrench } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { queryLoadFailure } from '../../lib/load-failure';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import FleetRegistryScreen, { isServiceDue, useFleetUnits } from './FleetRegistryScreen';
import FleetExpensesScreen from './FleetExpensesScreen';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useAuth } from '../../contexts/AuthContext';
import { haulingEyebrow } from '../../lib/hauling';

type FleetTab = 'units' | 'expenses';

/** `trucking_fleet.type` is free text; these are the values in use (and what Import writes). */
const UNIT_TYPES = [
  { value: 'truck', label: 'Truck' },
  { value: 'trailer', label: 'Trailer' },
];

const inputClass = 'w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none';

/**
 * ADD TRUCK WRITES TO THE FLEET THIS PAGE LISTS (#815).
 *
 * It used to POST to `/trucking/trucks`: the legacy `trucks` table, which this
 * page's list (`trucking_fleet`) never reads, with a snake_case `unit_number`
 * the route rejects. Now it is POST /trucking/fleet, camelCase: `unitNumber`
 * and `type` (both required), then `plate`, `make`, `model`, `year`, `vin`
 * when given. The old form's load types, free-text driver name, tare and max
 * payload have no column on `trucking_fleet`, so they are gone; a driver is
 * assigned from the unit's own record.
 */

export default function TrucksScreen() {
  const { hasVertical } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  // Redirect legacy 'fleet' and 'registry' tab params to 'units'
  const activeTab: FleetTab = rawTab === 'expenses' ? 'expenses' : 'units';
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [unitNumber, setUnitNumber] = useState('');
  const [unitType, setUnitType] = useState('');
  const [licensePlate, setLicensePlate] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [vin, setVin] = useState('');

  // The band counts the units on the active roster (retired excluded, as the route does).
  const fleetQuery = useFleetUnits(false);
  const fleetFailed = queryLoadFailure(fleetQuery) != null;
  const fleetLoading = fleetQuery.isLoading && !fleetFailed;
  const units = fleetFailed ? undefined : fleetQuery.data;
  // "Due" is counted against the TENANT's today, as on the register below.
  const timeZone = useTenantTimeZone();
  const tally = (match: (u: NonNullable<typeof units>[number]) => boolean) =>
    displayValue(units ? units.filter(match).length : null);

  const resetForm = () => {
    setUnitNumber(''); setUnitType(''); setLicensePlate('');
    setMake(''); setModel(''); setYear(''); setVin(''); setError('');
  };

  const handleSave = async () => {
    if (!unitNumber.trim() || !unitType) return;
    const body: Record<string, unknown> = { unitNumber: unitNumber.trim(), type: unitType };
    if (licensePlate.trim()) body.plate = licensePlate.trim();
    if (make.trim()) body.make = make.trim();
    if (model.trim()) body.model = model.trim();
    if (vin.trim()) body.vin = vin.trim();
    if (year.trim()) {
      const y = Number(year);
      if (!Number.isInteger(y) || y < 1900 || y > 2100) { setError('Year must be a four-digit year.'); return; }
      body.year = y;
    }
    setSaving(true); setError('');
    try {
      await apiClient.post<TruckingFleetCreateResponse>('/trucking/fleet', body);
      setShowForm(false); resetForm();
      // The list this page reads.
      qc.invalidateQueries({ queryKey: ['trucking-fleet'] });
    } catch (err: any) {
      // The API's own sentence: a duplicate unit number or VIN is a 409 that names it.
      setError(err.response?.data?.error || 'Failed to save truck');
    } finally { setSaving(false); }
  };

  const setTab = (tab: FleetTab) => {
    if (tab === 'units') {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };

  const tabClass = (on: boolean) =>
    `-mb-px border-b-2 px-1 pb-2 text-sm font-extrabold transition-colors ${
      on ? 'border-fi-amber text-fieldiq-text' : 'border-transparent text-fieldiq-muted hover:text-fieldiq-text'
    }`;

  return (
    <div data-testid="fleet-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={haulingEyebrow(hasVertical)}
        title="Fleet"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          activeTab === 'units' ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/import/fleet')}
                className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
              >
                <Upload className="size-4" aria-hidden="true" />
                Import
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(!showForm); setError(''); }}
                className="fi-action fi-action--standard fi-action--primary"
              >
                {showForm ? <X className="size-4" aria-hidden="true" /> : <Plus className="size-4" aria-hidden="true" />}
                {showForm ? 'Cancel' : 'Add Truck'}
              </button>
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Units', sub: 'Not retired', tone: 'neutral', loading: fleetLoading, value: tally(() => true) },
          { icon: <CheckCircle2 className="size-4" aria-hidden="true" />, label: 'Active', sub: 'In service', tone: 'success', loading: fleetLoading, value: tally((u) => u.status === 'active') },
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Service Due', sub: 'Date reached', tone: 'accent', loading: fleetLoading, value: tally((u) => isServiceDue(u, timeZone)) },
          { icon: <Wrench className="size-4" aria-hidden="true" />, label: 'Maintenance', sub: 'In the shop', tone: 'info', loading: fleetLoading, value: tally((u) => u.status === 'maintenance') },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {/* Tabs, not chips: units and expenses are different lists with different controls. */}
          <div role="tablist" aria-label="Fleet views" className="flex gap-6 border-b border-fieldiq-border">
            {([
              { key: 'units' as FleetTab, label: 'Units' },
              { key: 'expenses' as FleetTab, label: 'Expenses' },
            ]).map(tab => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setTab(tab.key)}
                className={tabClass(activeTab === tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'units' && (
            <>
              {showForm && (
                <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 animate-fade-in">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-bold text-fieldiq-text">Add Truck</span>
                    <button type="button" aria-label="Close" onClick={() => { setShowForm(false); resetForm(); }} className="text-fieldiq-muted hover:text-fieldiq-text"><X size={16} /></button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <div className="mb-2 text-xs uppercase tracking-wider text-fieldiq-muted">Identification</div>
                      <div className="grid grid-cols-2 gap-2">
                        <input className={inputClass} aria-label="Unit number" placeholder="e.g. T-009" value={unitNumber} onChange={e => setUnitNumber(e.target.value)} />
                        <select className={inputClass} aria-label="Unit type" value={unitType} onChange={e => setUnitType(e.target.value)}>
                          <option value="">Type…</option>
                          {UNIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 text-xs uppercase tracking-wider text-fieldiq-muted">Plate &amp; VIN</div>
                      <div className="grid grid-cols-2 gap-2">
                        <input className={inputClass} aria-label="Plate" placeholder="e.g. ABC 1234" value={licensePlate} onChange={e => setLicensePlate(e.target.value)} />
                        <input className={inputClass} aria-label="VIN" placeholder="VIN" value={vin} onChange={e => setVin(e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <div className="mb-2 text-xs uppercase tracking-wider text-fieldiq-muted">Vehicle</div>
                      <div className="mb-2 grid grid-cols-2 gap-2">
                        <input className={inputClass} aria-label="Make" placeholder="Make" value={make} onChange={e => setMake(e.target.value)} />
                        <input className={inputClass} aria-label="Model" placeholder="Model" value={model} onChange={e => setModel(e.target.value)} />
                      </div>
                      <input className={inputClass} type="number" aria-label="Year" placeholder="e.g. 2019" value={year} onChange={e => setYear(e.target.value)} />
                    </div>
                    {error && <div role="alert" className="rounded-fi-control border border-fieldiq-danger/30 bg-fieldiq-danger/10 px-3 py-2 text-sm text-fieldiq-text">{error}</div>}
                    <div className="flex gap-3">
                      <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="flex-1 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark py-2.5 font-semibold text-fieldiq-text">Cancel</button>
                      <button type="button" disabled={!unitNumber.trim() || !unitType || saving} onClick={handleSave} className="fi-action fi-action--standard fi-action--primary flex-1 justify-center disabled:opacity-50">
                        {saving && <Loader2 size={14} className="animate-spin" />}{saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <FleetRegistryScreen />
            </>
          )}

          {activeTab === 'expenses' && <FleetExpensesScreen embedded />}
        </div>
      </section>
    </div>
  );
}
